import React, { useEffect, useMemo, useState } from "react";
import {
  Printer,
  Power,
  Plus,
  RefreshCw,
  MapPin,
  Settings,
  Wifi,
  ShieldCheck,
  X,
  AlertTriangle,
  Volume2,
} from "lucide-react";
import { showToast } from "../utils/toast";
import { buildReceiptPrinterForm } from "../utils/receiptPrinterSettings";

const roles = [
  { id: "kitchen", label: "Cuisine", accent: "from-amber-400/20 to-orange-500/20" },
  { id: "bar", label: "Bar", accent: "from-sky-400/20 to-blue-500/20" },
  { id: "receipt", label: "Ticket caisse", accent: "from-emerald-400/20 to-green-500/20" },
  { id: "other", label: "Divers", accent: "from-purple-400/20 to-indigo-500/20" },
];

const normalizeReceiptConnectionType = (value) => {
  const raw = String(value || "").toLowerCase();
  if (raw === "usb") return "usb";
  if (raw === "direct_rj45") return "direct_rj45";
  return "ethernet";
};

const isReceiptNetworkConnection = (connectionType) =>
  normalizeReceiptConnectionType(connectionType) !== "usb";

const normalizeCopies = (value) => {
  const copies = Math.floor(Number(value));
  return Number.isFinite(copies) && copies >= 1 ? copies : 1;
};

const PrinterManagement = () => {
  const getPreferredDark = () => {
    if (typeof document === "undefined") return false;
    const docHasDark = document.documentElement.classList.contains("dark");
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem("theme") : null;
    const media = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
    return stored ? stored === "dark" : docHasDark || media;
  };

  const [printers, setPrinters] = useState([]);
  const [kdsSettings, setKdsSettings] = useState({
    bar_enabled: 1,
    kitchen_enabled: 1,
    dual_screen_enabled: 0,
  });
  const [productionDestinations, setProductionDestinations] = useState([]);
  const [destinationModalOpen, setDestinationModalOpen] = useState(false);
  const [destinationForm, setDestinationForm] = useState({
    name: "",
    color: "#8b5cf6",
    icon: "📺",
  });
  const [receiptSettings, setReceiptSettings] = useState({
    connection_type: "ethernet",
    printer_name: "Ticket caisse",
    ip_address: "",
    port: 9100,
    copies: 1,
    paper_width_mm: 80,
  });
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    connection_type: "ethernet",
    printer_name: "Ticket caisse",
    ip_address: "",
    port: 9100,
    copies: 1,
    paper_width_mm: 80,
  });
  const [receiptIpParts, setReceiptIpParts] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    role: "kitchen",
    ip_address: "",
    port: 9100,
    copies: 1,
    is_active: true,
  });
  const [ipParts, setIpParts] = useState(["", "", "", ""]);
  const [isDarkMode, setIsDarkMode] = useState(getPreferredDark);
  const [rhodeModalTarget, setRhodeModalTarget] = useState(null);
  const [rhodeForm, setRhodeForm] = useState({
    enabled: true,
    announce_server_name: true,
    announce_repeat_table: true,
    announce_items_with_qty: true,
    announce_delay_reminder: true,
    delay_initial_minutes: 10,
    delay_repeat_minutes: 5,
    volume: 0.8,
    rate: 1,
    pitch: 1,
  });

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(getPreferredDark());
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    syncTheme();
    const media = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    media?.addEventListener("change", syncTheme);
    return () => {
      observer.disconnect();
      media?.removeEventListener("change", syncTheme);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [printersRes, kdsRes, receiptRes, destinationsRes] = await Promise.all([
        fetch("/api/printers"),
        fetch("/api/kds-settings"),
        fetch("/api/receipt-printer-settings"),
        fetch("/api/production-destinations"),
      ]);
      if (!printersRes.ok) throw new Error("Impossible de charger les imprimantes");
      if (!kdsRes.ok) throw new Error("Impossible de charger les réglages KDS");
      if (!receiptRes.ok) throw new Error("Impossible de charger l'imprimante ticket caisse");
      if (!destinationsRes.ok) throw new Error("Impossible de charger les destinations KDS");
      const printersData = await printersRes.json();
      const kdsData = await kdsRes.json();
      const receiptData = await receiptRes.json();
      const destinationsData = await destinationsRes.json();
      setProductionDestinations(Array.isArray(destinationsData) ? destinationsData : []);
      setPrinters(printersData || []);
      setKdsSettings(
        kdsData || { bar_enabled: 1, kitchen_enabled: 1, dual_screen_enabled: 0 },
      );
      const normalizedReceipt = receiptData || {
        connection_type: "ethernet",
        printer_name: "Ticket caisse",
        ip_address: "",
        port: 9100,
        copies: 1,
        paper_width_mm: 80,
      };
      const normalizedConnectionType = normalizeReceiptConnectionType(
        normalizedReceipt.connection_type,
      );
      const normalizedReceiptSettings = {
        ...normalizedReceipt,
        connection_type: normalizedConnectionType,
      };
      setReceiptSettings(normalizedReceiptSettings);
      setReceiptForm(buildReceiptPrinterForm(normalizedReceiptSettings));
      const parts = String(normalizedReceiptSettings.ip_address || "").split(".");
      setReceiptIpParts([parts[0] || "", parts[1] || "", parts[2] || "", parts[3] || ""]);
    } catch (error) {
      console.error("❌ Impression:", error);
      showToast("error", error.message || "Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const groupedPrinters = useMemo(() => {
    const customRoles = productionDestinations
      .filter((destination) =>
        Boolean(Number(destination.is_active ?? 1)) &&
        !["none", "cuisine", "bar"].includes(destination.code),
      )
      .map((destination) => ({
        id: destination.code,
        label: destination.name,
        accent: "from-violet-400/20 to-fuchsia-500/20",
        custom: true,
      }));
    return [...roles, ...customRoles].map((role) => ({
      ...role,
      printers: printers.filter((printer) => {
        const configuredTarget = String(printer.production_destination_code || "").trim();
        return role.custom
          ? configuredTarget === role.id
          : !configuredTarget && printer.role === role.id;
      }),
    }));
  }, [printers, productionDestinations]);

  const printerTargetOptions = useMemo(
    () => [
      ...roles.map((role) => ({ value: role.id, label: role.label })),
      ...productionDestinations
        .filter((destination) =>
          Boolean(Number(destination.is_active ?? 1)) &&
          !["none", "cuisine", "bar"].includes(destination.code),
        )
        .map((destination) => ({
          value: destination.code,
          label: destination.name,
        })),
    ],
    [productionDestinations],
  );

  const toggleKds = async (key) => {
    try {
      const payload = {
        ...kdsSettings,
        [key]: kdsSettings[key] ? 0 : 1,
      };
      setKdsSettings(payload);
      const res = await fetch("/api/kds-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Impossible de mettre à jour le KDS");
      const label =
        key === "bar_enabled"
          ? "KDS bar"
          : key === "kitchen_enabled"
            ? "KDS cuisine"
            : "Double écran";
      showToast("success", `${label} mis à jour`);
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur KDS");
    }
  };

  const toggleProductionDestination = async (destination) => {
    try {
      const response = await fetch(
        `/api/production-destinations/${encodeURIComponent(destination.code)}/toggle`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled: !Boolean(Number(destination.kds_enabled)) }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Mise à jour impossible");
      setProductionDestinations((current) =>
        current.map((entry) => entry.code === payload.code ? payload : entry),
      );
      showToast("success", `KDS ${payload.name} mis à jour`);
    } catch (error) {
      showToast("error", error?.message || "Erreur KDS");
    }
  };

  const createProductionDestination = async () => {
    const name = String(destinationForm.name || "").trim();
    if (!name) {
      showToast("error", "Le nom de la destination est requis");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/production-destinations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...destinationForm, kds_enabled: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Création impossible");
      setDestinationModalOpen(false);
      setDestinationForm({ name: "", color: "#8b5cf6", icon: "📺" });
      await fetchData();
      showToast("success", `Destination ${payload.name} créée`);
    } catch (error) {
      showToast("error", error?.message || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  const deleteProductionDestination = async (destination) => {
    if (!window.confirm(`Supprimer la destination « ${destination.name} » ?`)) return;
    try {
      const response = await fetch(
        `/api/production-destinations/${encodeURIComponent(destination.code)}`,
        { method: "DELETE" },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Suppression impossible");
      await fetchData();
      showToast("success", "Destination supprimée");
    } catch (error) {
      showToast("error", error?.message || "Suppression impossible");
    }
  };

  const openRhodeModal = (target) => {
    const isBar = target === "bar";
    const config = isBar ? kdsSettings.rhode_bar_config : kdsSettings.rhode_kitchen_config;
    const enabled = isBar
      ? Boolean(kdsSettings.rhode_bar_enabled ?? 1)
      : Boolean(kdsSettings.rhode_kitchen_enabled ?? 1);
    setRhodeForm({
      enabled,
      announce_server_name: Boolean(config?.announce_server_name ?? true),
      announce_repeat_table: Boolean(config?.announce_repeat_table ?? true),
      announce_items_with_qty: Boolean(config?.announce_items_with_qty ?? true),
      announce_delay_reminder: Boolean(config?.announce_delay_reminder ?? true),
      delay_initial_minutes: Number(config?.delay_initial_minutes ?? 10),
      delay_repeat_minutes: Number(config?.delay_repeat_minutes ?? 5),
      volume: Number(config?.volume ?? 0.8),
      rate: Number(config?.rate ?? 1),
      pitch: Number(config?.pitch ?? 1),
    });
    setRhodeModalTarget(target);
  };

  const saveRhodeConfig = async () => {
    if (!rhodeModalTarget) return;
    setSaving(true);
    try {
      const isBar = rhodeModalTarget === "bar";
      const toNumberOrDefault = (value, fallback) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : fallback;
      };
      const payload = {
        ...kdsSettings,
        rhode_bar_enabled: isBar
          ? (rhodeForm.enabled ? 1 : 0)
          : Number(kdsSettings.rhode_bar_enabled ?? 1),
        rhode_kitchen_enabled: isBar
          ? Number(kdsSettings.rhode_kitchen_enabled ?? 1)
          : (rhodeForm.enabled ? 1 : 0),
        rhode_bar_config: isBar
          ? {
              announce_server_name: rhodeForm.announce_server_name,
              announce_repeat_table: rhodeForm.announce_repeat_table,
              announce_items_with_qty: rhodeForm.announce_items_with_qty,
              announce_delay_reminder: rhodeForm.announce_delay_reminder,
              delay_initial_minutes: toNumberOrDefault(rhodeForm.delay_initial_minutes, 10),
              delay_repeat_minutes: toNumberOrDefault(rhodeForm.delay_repeat_minutes, 5),
              volume: toNumberOrDefault(rhodeForm.volume, 0.8),
              rate: toNumberOrDefault(rhodeForm.rate, 1),
              pitch: toNumberOrDefault(rhodeForm.pitch, 1),
            }
          : kdsSettings.rhode_bar_config,
        rhode_kitchen_config: isBar
          ? kdsSettings.rhode_kitchen_config
          : {
              announce_server_name: rhodeForm.announce_server_name,
              announce_repeat_table: rhodeForm.announce_repeat_table,
              announce_items_with_qty: rhodeForm.announce_items_with_qty,
              announce_delay_reminder: rhodeForm.announce_delay_reminder,
              delay_initial_minutes: toNumberOrDefault(rhodeForm.delay_initial_minutes, 10),
              delay_repeat_minutes: toNumberOrDefault(rhodeForm.delay_repeat_minutes, 5),
              volume: toNumberOrDefault(rhodeForm.volume, 0.8),
              rate: toNumberOrDefault(rhodeForm.rate, 1),
              pitch: toNumberOrDefault(rhodeForm.pitch, 1),
            },
      };
      const res = await fetch("/api/kds-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Impossible de sauvegarder Rhode");
      const next = await res.json();
      setKdsSettings(next);
      // Relecture directe pour éviter toute incohérence d'affichage après sauvegarde
      try {
        const refresh = await fetch("/api/kds-settings");
        if (refresh.ok) {
          const refreshedData = await refresh.json();
          setKdsSettings(refreshedData);
        }
      } catch (_) {
        // best effort
      }
      showToast("success", "Paramètres Rhode enregistrés");
      setRhodeModalTarget(null);
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur sauvegarde Rhode");
    } finally {
      setSaving(false);
    }
  };

  const openModal = (printer = null) => {
    if (printer) {
      setEditing(printer);
      setForm({
        name: printer.name || "",
        role: printer.production_destination_code || printer.role || "kitchen",
        ip_address: printer.ip_address || "",
        port: printer.port || 9100,
        copies: normalizeCopies(printer.copies),
        is_active: Boolean(printer.is_active),
      });
      const parts = (printer.ip_address || "").split(".");
      setIpParts([parts[0] || "", parts[1] || "", parts[2] || "", parts[3] || ""]);
    } else {
      setEditing(null);
      setForm({
        name: "",
        role: "kitchen",
        ip_address: "",
        port: 9100,
        copies: 1,
        is_active: true,
      });
      setIpParts(["", "", "", ""]);
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    const ipFromParts = ipParts.filter((p) => p !== "").join(".");
    const ipAddress = form.ip_address || ipFromParts;
    if (!form.name || !ipAddress) {
      showToast("error", "Nom et IP sont requis");
      return;
    }
    // Validation simple des octets
    const octets = ipAddress.split(".");
    if (octets.length !== 4 || octets.some((o) => Number.isNaN(Number(o)) || Number(o) < 0 || Number(o) > 255)) {
      showToast("error", "Adresse IP invalide");
      return;
    }
    setSaving(true);
    try {
      const isStandardTarget = roles.some((role) => role.id === form.role);
      const persistedRole = isStandardTarget ? form.role : "other";
      const payload = {
        ...form,
        role: persistedRole,
        ip_address: ipAddress,
        port: Number(form.port) || 9100,
        copies: normalizeCopies(form.copies),
        kds_target: ["kitchen", "bar"].includes(persistedRole) ? persistedRole : "none",
        production_destination_code: isStandardTarget ? null : form.role,
        // Les anciennes bases limitent encore `type` à cuisine/bar/ticket.
        // Le routage personnalisé repose sur production_destination_code.
        type: ["kitchen", "bar", "receipt"].includes(persistedRole) ? persistedRole : "receipt",
        location: "",
        notes: "",
      };
      const method = editing ? "PUT" : "POST";
      const url = editing ? `/api/printers/${editing.id}` : "/api/printers";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Impossible d'enregistrer l'imprimante");
      }
      showToast("success", "Imprimante enregistrée");
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (printer) => {
    if (!window.confirm(`Supprimer ${printer.name} ?`)) return;
    try {
      const res = await fetch(`/api/printers/${printer.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Suppression impossible");
      showToast("success", "Imprimante supprimée");
      fetchData();
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur suppression");
    }
  };

  const handleTest = async (printer) => {
    if (!printer.is_active) {
      showToast("error", "Imprimante désactivée");
      return;
    }
    try {
      const res = await fetch("/api/printers/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ip: printer.ip_address,
          port: Number(printer.port) || 9100,
          message: "Bienvenue sur MOO Sync. Imprimante bien détecté.",
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Test impossible");
      }
      showToast(
        "success",
        `Test envoyé à ${printer.name} (${printer.ip_address}:${printer.port || 9100})`,
      );
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Échec du test d'impression");
    }
  };

  const handleToggleActive = async (printer) => {
    try {
      const payload = {
        ...printer,
        is_active: printer.is_active ? 0 : 1,
      };
      const res = await fetch(`/api/printers/${printer.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Impossible de mettre à jour l'imprimante");
      }
      showToast("success", `Imprimante ${payload.is_active ? "activée" : "désactivée"}`);
      fetchData();
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur lors de l'activation");
    }
  };

  const openReceiptModal = () => {
    const current = receiptSettings || {};
    setReceiptForm(buildReceiptPrinterForm(current));
    const parts = String(current.ip_address || "").split(".");
    setReceiptIpParts([parts[0] || "", parts[1] || "", parts[2] || "", parts[3] || ""]);
    setReceiptModalOpen(true);
  };

  const saveReceiptSettings = async () => {
    const connection_type = normalizeReceiptConnectionType(receiptForm.connection_type);
    const isNetworkMode = isReceiptNetworkConnection(connection_type);
    const ipFromParts = receiptIpParts.filter((p) => p !== "").join(".");
    const ip_address = receiptForm.ip_address || ipFromParts;
    const printer_name = receiptForm.printer_name?.trim() || "Ticket caisse";
    const port = Number(receiptForm.port) || 9100;
    const copies = normalizeCopies(receiptForm.copies);
    const paper_width_mm = Number(receiptForm.paper_width_mm) === 58 ? 58 : 80;

    if (isNetworkMode) {
      if (!ip_address) {
        showToast("error", "Adresse IP requise");
        return;
      }
      const octets = ip_address.split(".");
      if (
        octets.length !== 4 ||
        octets.some((o) => Number.isNaN(Number(o)) || Number(o) < 0 || Number(o) > 255)
      ) {
        showToast("error", "Adresse IP invalide");
        return;
      }
    }

    setSaving(true);
    try {
      const res = await fetch("/api/receipt-printer-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          connection_type,
          printer_name,
          ip_address: isNetworkMode ? ip_address : null,
          port: isNetworkMode ? port : null,
          copies,
          paper_width_mm,
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        if (
          txt &&
          typeof txt === "string" &&
          txt.includes("Cannot POST /api/receipt-printer-settings")
        ) {
          throw new Error(
            "Le serveur API ne connaît pas encore cet endpoint. Redémarre le serveur (ex: `npm run dev` ou `npm run server`) puis réessaie.",
          );
        }
        throw new Error(txt || "Impossible de sauvegarder");
      }
      const data = await res.json();
      setReceiptSettings(data);
      setReceiptModalOpen(false);
      showToast("success", "Ticket caisse configuré");
      fetchData();
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const testReceiptPrinter = async () => {
    const connection_type = normalizeReceiptConnectionType(receiptForm.connection_type);
    const isNetworkMode = isReceiptNetworkConnection(connection_type);
    const isDirectRj45 = connection_type === "direct_rj45";
    if (!isNetworkMode) {
      showToast("info", "Test IP disponible uniquement en mode Ethernet IP ou Direct RJ45");
      return;
    }
    const ipFromParts = receiptIpParts.filter((p) => p !== "").join(".");
    const ip_address = receiptForm.ip_address || ipFromParts;
    const port = Number(receiptForm.port) || 9100;
    if (!ip_address) {
      showToast("error", "Adresse IP requise");
      return;
    }
    try {
      if (isDirectRj45) {
        try {
          const diagRes = await fetch("/api/receipt-printer/direct-rj45-diagnose", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ip: ip_address, port }),
          });
          const diagData = await diagRes.json().catch(() => ({}));
          const warnings = Array.isArray(diagData?.warnings)
            ? diagData.warnings.filter(Boolean)
            : [];
          if (warnings.length > 0) {
            showToast("warning", `Diagnostic RJ45 direct: ${warnings[0]}`);
          } else if (diagRes.ok) {
            showToast("success", "Diagnostic RJ45 direct: liaison locale OK");
          } else {
            showToast("warning", diagData?.error || "Diagnostic RJ45 direct indisponible");
          }
        } catch (diagError) {
          console.error(diagError);
          showToast(
            "warning",
            diagError?.message || "Diagnostic RJ45 direct indisponible (test impression poursuivi)",
          );
        }
      }

      const res = await fetch("/api/printers/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip: ip_address, port }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Test impossible");
      }
      if (isDirectRj45) {
        showToast("success", "Test Direct RJ45 envoye a limprimante ticket caisse");
      } else {
        showToast("success", "Test envoye a limprimante ticket caisse");
      }
    } catch (error) {
      console.error(error);
      showToast("error", error.message || "Echec du test");
    }
  };

  return (
    <div
      className={`min-h-screen ${
        isDarkMode
          ? "bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
          : "bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <header className={`${cardBg(isDarkMode)} rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4 shadow-xl`}>
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-300">Paramètres</p>
            <h1 className="text-3xl font-bold">Gestion des Imprimantes & KDS</h1>
            <p className="text-sm opacity-70">
              Créez vos zones de production et rattachez les imprimantes IP associées.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={fetchData}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </button>
            <button
              type="button"
              onClick={() => setDestinationModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-2xl border border-amber-400/50 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-400/10"
            >
              <Plus className="w-4 h-4" />
              Destination KDS
            </button>
            <button
              type="button"
              onClick={() => openModal(null)}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-white shadow-lg hover:from-orange-600 hover:to-amber-600"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KdsToggleCard
            title="KDS Bar"
            description="Interface Bar pour afficher les commandes boissons."
            enabled={Boolean(kdsSettings.bar_enabled)}
            onToggle={() => toggleKds("bar_enabled")}
            onVoiceConfig={() => openRhodeModal("bar")}
            isDarkMode={isDarkMode}
          />
          {productionDestinations
            .filter((destination) => !["none", "cuisine", "bar"].includes(destination.code))
            .map((destination) => (
              <KdsToggleCard
                key={destination.code}
                title={`KDS ${destination.name}`}
                description={`Interface /kds/${destination.code}`}
                enabled={Boolean(Number(destination.kds_enabled))}
                onToggle={() => toggleProductionDestination(destination)}
                onOpen={() => window.open(`/kds/${destination.code}`, "_blank")}
                onDelete={() => deleteProductionDestination(destination)}
                isDarkMode={isDarkMode}
              />
            ))}
          <KdsToggleCard
            title="KDS Cuisine"
            description="Interface Cuisine pour afficher les plats."
            enabled={Boolean(kdsSettings.kitchen_enabled)}
            onToggle={() => toggleKds("kitchen_enabled")}
            onVoiceConfig={() => openRhodeModal("kitchen")}
            isDarkMode={isDarkMode}
          />
          <KdsToggleCard
            title="Double écran client"
            description="Active l’affichage client (prix/commande) sur un second écran."
            enabled={Boolean(kdsSettings.dual_screen_enabled)}
            onToggle={() => toggleKds("dual_screen_enabled")}
            isDarkMode={isDarkMode}
          />
          <ReceiptPrinterCard
            isDarkMode={isDarkMode}
            settings={receiptSettings}
            onConfigure={openReceiptModal}
          />
        </section>

        <section className={`${cardBg(isDarkMode)} rounded-3xl p-6 shadow-lg space-y-6`}>
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-amber-300" />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Imprimantes IP</p>
              <h2 className="text-xl font-semibold">Routage par zone</h2>
            </div>
          </div>

          {loading ? (
            <p className="text-sm opacity-60">Chargement des imprimantes…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {groupedPrinters.map((group) => (
                <div
                  key={group.id}
                  className={`rounded-2xl border p-4 space-y-3 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{group.label}</p>
                      <p className="text-sm opacity-70">{group.printers.length} imprimante(s)</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs bg-gradient-to-r ${group.accent}`}>
                      {group.label}
                    </span>
                  </div>
                  {group.printers.length === 0 ? (
                    <p className="text-sm opacity-60">Aucune imprimante configurée.</p>
                  ) : (
                    <div className="space-y-3">
                      {group.printers.map((printer) => (
                        <PrinterCard
                          key={printer.id}
                          printer={printer}
                          targetLabel={group.label}
                          isDarkMode={isDarkMode}
                          onEdit={() => openModal(printer)}
                          onDelete={() => handleDelete(printer)}
                          onTest={() => handleTest(printer)}
                          onToggle={() => handleToggleActive(printer)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {modalOpen && (
        <PrinterModal
          isDarkMode={isDarkMode}
          form={form}
          ipParts={ipParts}
          setIpParts={setIpParts}
          onChange={setForm}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          onTest={async () => {
            if (!form.ip_address) {
              showToast("error", "Adresse IP requise pour tester");
              return;
            }
            try {
              const res = await fetch("/api/printers/test", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  ip: form.ip_address,
                  port: Number(form.port) || 9100,
                  message: "Bienvenue sur MOO Sync. Imprimante bien détecté.",
                }),
              });
              if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Test impossible");
              }
              showToast("success", "Test envoyé à l'imprimante");
            } catch (error) {
              console.error(error);
              showToast("error", error.message || "Échec du test d'impression");
            }
          }}
          saving={saving}
          isEdit={Boolean(editing)}
          targetOptions={printerTargetOptions}
        />
      )}

      {receiptModalOpen && (
        <ReceiptPrinterModal
          isDarkMode={isDarkMode}
          form={receiptForm}
          setForm={setReceiptForm}
          ipParts={receiptIpParts}
          setIpParts={setReceiptIpParts}
          onClose={() => setReceiptModalOpen(false)}
          onSave={saveReceiptSettings}
          onTest={testReceiptPrinter}
          saving={saving}
        />
      )}

      {rhodeModalTarget && (
        <RhodeConfigModal
          isDarkMode={isDarkMode}
          target={rhodeModalTarget}
          form={rhodeForm}
          setForm={setRhodeForm}
          onClose={() => setRhodeModalTarget(null)}
          onSave={saveRhodeConfig}
          saving={saving}
        />
      )}
      {destinationModalOpen && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`${cardBg(isDarkMode)} w-full max-w-lg rounded-3xl p-6 space-y-5 shadow-2xl`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Nouvelle zone</p>
                <h3 className="text-xl font-semibold">Ajouter une destination KDS</h3>
              </div>
              <button type="button" onClick={() => setDestinationModalOpen(false)} className="rounded-xl p-2 border border-white/10"><X className="w-5 h-5" /></button>
            </div>
            <label className="block space-y-2">
              <span className="text-sm font-semibold">Nom</span>
              <input value={destinationForm.name} onChange={(event) => setDestinationForm((current) => ({ ...current, name: event.target.value }))} placeholder="Ex. Pâtisserie, Grill, Froid" className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block space-y-2"><span className="text-sm font-semibold">Icône</span><input value={destinationForm.icon} onChange={(event) => setDestinationForm((current) => ({ ...current, icon: event.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3" /></label>
              <label className="block space-y-2"><span className="text-sm font-semibold">Couleur</span><input type="color" value={destinationForm.color} onChange={(event) => setDestinationForm((current) => ({ ...current, color: event.target.value }))} className="h-12 w-full rounded-2xl border border-white/10 bg-white/5 px-2" /></label>
            </div>
            <button type="button" disabled={saving} onClick={createProductionDestination} className="w-full rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 py-3 font-bold text-slate-950 disabled:opacity-50">Créer la destination</button>
          </div>
        </div>
      )}
    </div>
  );
};

const cardBg = (isDark) =>
  isDark ? "bg-[#0f172a]/80 border border-white/10" : "bg-white border border-slate-100";

const KdsToggleCard = ({ title, description, enabled, onToggle, onVoiceConfig, onOpen, onDelete, isDarkMode }) => (
  <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 shadow-lg flex items-center justify-between gap-4`}>
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-amber-300">KDS</p>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm opacity-70">{description}</p>
    </div>
    <div className="flex items-center gap-2">
      {typeof onVoiceConfig === "function" && (
        <button
          type="button"
          onClick={onVoiceConfig}
          className="inline-flex items-center justify-center rounded-2xl px-3 py-2 text-sm font-semibold bg-white/5 text-white/80 border border-white/10 hover:border-amber-400/60"
          title="Configurer Rhode"
        >
          <Volume2 className="w-4 h-4" />
        </button>
      )}
      {typeof onOpen === "function" && (
        <button type="button" onClick={onOpen} className="rounded-2xl px-3 py-2 text-sm font-semibold border border-white/10 hover:border-amber-400/60">Ouvrir</button>
      )}
      {typeof onDelete === "function" && (
        <button type="button" onClick={onDelete} className="rounded-2xl px-3 py-2 text-sm font-semibold border border-rose-400/30 text-rose-300 hover:bg-rose-400/10">Supprimer</button>
      )}
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold ${
          enabled
            ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/50"
            : "bg-white/5 text-white/70 border border-white/10"
        }`}
      >
        <Power className={`w-4 h-4 ${enabled ? "text-emerald-300" : "text-white/60"}`} />
        {enabled ? "Activé" : "Désactivé"}
      </button>
    </div>
  </div>
);

const RhodeConfigModal = ({ isDarkMode, target, form, setForm, onClose, onSave, saving }) => {
  const title = target === "bar" ? "Rhode • KDS Bar" : "Rhode • KDS Cuisine";
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`${cardBg(isDarkMode)} w-full max-w-2xl rounded-3xl shadow-2xl border border-white/10`}>
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Assistant vocal</p>
            <h3 className="text-xl font-semibold">{title}</h3>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 border border-white/10 hover:bg-white/5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <label className="flex items-center justify-between p-4 rounded-2xl border border-white/10 bg-white/5">
            <span className="font-semibold">Activer Rhode</span>
            <input
              type="checkbox"
              checked={Boolean(form.enabled)}
              onChange={(e) => update("enabled", e.target.checked)}
              className="h-5 w-5"
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["announce_server_name", "Énoncer le nom du serveur"],
              ["announce_repeat_table", "Énoncer commande additionnelle"],
              ["announce_items_with_qty", "Énoncer articles + quantités"],
              ["announce_delay_reminder", "Activer rappels de retard"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/5">
                <span className="text-sm">{label}</span>
                <input
                  type="checkbox"
                  checked={Boolean(form[key])}
                  onChange={(e) => update(key, e.target.checked)}
                  className="h-4 w-4"
                />
              </label>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs uppercase tracking-[0.2em] opacity-70">Rappel initial (min)</span>
              <input
                type="number"
                min={1}
                max={120}
                value={form.delay_initial_minutes}
                onChange={(e) => update("delay_initial_minutes", Number(e.target.value || 10))}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs uppercase tracking-[0.2em] opacity-70">Rappel suivant (min)</span>
              <input
                type="number"
                min={1}
                max={60}
                value={form.delay_repeat_minutes}
                onChange={(e) => update("delay_repeat_minutes", Number(e.target.value || 5))}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs uppercase tracking-[0.2em] opacity-70">
                Volume ({Math.round(Number(form.volume || 0) * 100)}%)
              </span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={form.volume}
                onChange={(e) => update("volume", Number(e.target.value))}
                className="w-full"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs uppercase tracking-[0.2em] opacity-70">
                Vitesse ({Number(form.rate || 1).toFixed(1)}x)
              </span>
              <input
                type="range"
                min={0.5}
                max={2}
                step={0.1}
                value={form.rate}
                onChange={(e) => update("rate", Number(e.target.value))}
                className="w-full"
              />
            </label>
          </div>
          <label className="space-y-1">
            <span className="text-xs uppercase tracking-[0.2em] opacity-70">
              Tonalité ({Number(form.pitch || 1).toFixed(1)})
            </span>
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              value={form.pitch}
              onChange={(e) => update("pitch", Number(e.target.value))}
              className="w-full"
            />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2">Annuler</button>
          <button
            onClick={onSave}
            disabled={saving}
            className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReceiptPrinterCard = ({ isDarkMode, settings, onConfigure }) => {
  const connection = String(settings?.connection_type || "ethernet").toLowerCase();
  const isUsb = connection === "usb";
  const isDirectRj45 = connection === "direct_rj45";
  const modeLabel = isUsb ? "USB" : isDirectRj45 ? "Direct RJ45" : "Ethernet (IP)";
  const details = isUsb
    ? "Impression via l’imprimante par défaut du PC"
    : settings?.ip_address
      ? `${settings.ip_address}:${settings.port || 9100}`
      : "Aucune IP définie";

  return (
    <div className={`${cardBg(isDarkMode)} rounded-3xl p-5 shadow-lg flex items-center justify-between gap-4`}>
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Ticket caisse</p>
        <h3 className="text-lg font-semibold">Imprimante caisse</h3>
        <p className="text-sm opacity-70">
          Mode : {modeLabel} — {details}
        </p>
      </div>
      <button
        type="button"
        onClick={onConfigure}
        className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold bg-white/5 text-white/80 border border-white/10 hover:border-amber-400/60"
      >
        <Settings className="w-4 h-4" />
        Configurer
      </button>
    </div>
  );
};

const PrinterCard = ({ printer, targetLabel, isDarkMode, onEdit, onDelete, onTest, onToggle }) => (
  <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{targetLabel}</p>
        <h4 className="text-lg font-semibold">{printer.name}</h4>
        <p className="text-sm opacity-70 flex items-center gap-2">
          <Wifi className="w-4 h-4" />
          {printer.ip_address}:{printer.port}
        </p>
        {printer.location && (
          <p className="text-xs opacity-70 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {printer.location}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span
          className={`px-3 py-1 rounded-full text-xs ${
            printer.is_active ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white/70"
          }`}
        >
          {printer.is_active ? "Active" : "Inactive"}
        </span>
      </div>
    </div>
    <div className="flex items-center gap-2 mt-3 text-xs opacity-70">
      <ShieldCheck className="w-4 h-4" />
      Sortie : {targetLabel}
    </div>
    {printer.notes && <p className="text-xs opacity-70 mt-2">{printer.notes}</p>}
    <div className="flex gap-2 mt-4 text-sm">
      <button
        type="button"
        onClick={onToggle}
        className={`rounded-2xl border px-3 py-2 flex items-center gap-2 ${
          printer.is_active
            ? "border-emerald-400/60 hover:border-rose-400/60"
            : "border-white/10 hover:border-emerald-400/60"
        }`}
        title={printer.is_active ? "Désactiver" : "Activer"}
      >
        <Power className={`w-4 h-4 ${printer.is_active ? "text-emerald-300" : "text-white/80"}`} />
        {printer.is_active ? "Désactiver" : "Activer"}
      </button>
      <button
        type="button"
        onClick={onTest}
        disabled={!printer.is_active}
        className={`rounded-2xl border px-3 py-2 ${printer.is_active ? "border-white/10 hover:border-amber-400/60" : "border-white/5 opacity-50 cursor-not-allowed"}`}
      >
        Tester
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="rounded-2xl border border-white/10 px-3 py-2 hover:border-emerald-400/60"
      >
        Modifier
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="rounded-2xl border border-white/10 px-3 py-2 text-rose-300 hover:border-rose-400/60"
      >
        Supprimer
      </button>
    </div>
  </div>
);

const ReceiptPrinterModal = ({ isDarkMode, form, setForm, ipParts, setIpParts, onClose, onSave, onTest, saving }) => {
  const connectionType = normalizeReceiptConnectionType(form.connection_type);
  const isUsb = connectionType === "usb";
  const isDirectRj45 = connectionType === "direct_rj45";
  const isNetworkMode = !isUsb;
  const isElectron = typeof window !== "undefined" && Boolean(window.electronAPI);
  const updateIpPart = (index, value) => {
    const clean = String(value || "").replace(/[^0-9]/g, "").slice(0, 3);
    const next = [...ipParts];
    next[index] = clean;
    setIpParts(next);
    setForm((prev) => ({ ...prev, ip_address: "" }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
      <div className={`${cardBg(isDarkMode)} w-full max-w-xl rounded-3xl p-6 shadow-2xl`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-300">Ticket caisse</p>
            <h3 className="text-2xl font-bold">Configurer l’imprimante</h3>
            <p className="text-sm opacity-70">
              USB : imprime sur l'imprimante par defaut. Ethernet : imprimante reseau (IP). Direct RJ45 : imprimante branchee en direct au PC de caisse.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold">
                {isElectron ? "Desktop (Electron)" : "Navigateur"}
              </span>
              {isUsb && !isElectron && (
                <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-200">
                  En navigateur, la boîte de dialogue d’impression est obligatoire
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-11 rounded-xl bg-white/10 border border-white/10 hover:bg-white/20 flex items-center justify-center"
            title="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-2">Connexion</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, connection_type: "usb" }))}
                className={`flex-1 rounded-2xl border px-4 py-3 text-sm font-semibold ${isUsb ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
              >
                USB
              </button>
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, connection_type: "ethernet" }))}
                className={`flex-1 rounded-2xl border px-4 py-3 text-sm font-semibold ${connectionType === "ethernet" ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
              >
                Ethernet (IP)
              </button>
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, connection_type: "direct_rj45" }))}
                className={`flex-1 rounded-2xl border px-4 py-3 text-sm font-semibold ${isDirectRj45 ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
              >
                Direct RJ45
              </button>
            </div>
            {isUsb && (
              <>
                <p className="mt-2 text-xs opacity-70">
                  Astuce : pour une impression USB sans fenêtre (silencieuse), utilise l’app Desktop (Electron). En mode navigateur, c’est limité par la sécurité du navigateur.
                </p>
                <div className="mt-4">
                  <label className="text-sm font-semibold opacity-80">Largeur du papier</label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {[80, 58].map((width) => (
                      <button
                        key={width}
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, paper_width_mm: width }))}
                        className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${Number(form.paper_width_mm || 80) === width ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
                      >
                        {width} mm{width === 80 ? " (par défaut)" : ""}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {isDirectRj45 && (
              <p className="mt-2 text-xs opacity-70">
                En direct RJ45 : place le PC et l'imprimante dans le meme sous-reseau, puis teste l'IP:port.
              </p>
            )}
          </div>

          <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
            <label className="text-sm font-semibold opacity-80">Nom</label>
            <input
              value={form.printer_name || ""}
              onChange={(e) => setForm((p) => ({ ...p, printer_name: e.target.value }))}
              className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
              placeholder="Ticket caisse"
            />
          </div>

          <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
            <label className="text-sm font-semibold opacity-80">Nombre de copies</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.copies || 1}
              onChange={(e) => setForm((p) => ({ ...p, copies: normalizeCopies(e.target.value) }))}
              className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
              inputMode="numeric"
            />
            <p className="mt-2 text-xs opacity-70">
              Appliqué aux reçus et factures. Les rapports X/Z restent en 1 exemplaire.
            </p>
          </div>

          {isNetworkMode && (
            <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}>
              <label className="text-sm font-semibold opacity-80">Adresse IP</label>
              <div className="mt-2 flex items-center gap-2">
                {ipParts.map((part, idx) => (
                  <React.Fragment key={idx}>
                    <input
                      value={part}
                      onChange={(e) => updateIpPart(idx, e.target.value)}
                      className={`w-16 rounded-xl border px-2 py-2 text-center text-sm ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
                      inputMode="numeric"
                      placeholder="0"
                    />
                    {idx < 3 && <span className="opacity-60">.</span>}
                  </React.Fragment>
                ))}
                <input
                  value={form.port || 9100}
                  onChange={(e) => setForm((p) => ({ ...p, port: e.target.value }))}
                  className={`ml-auto w-24 rounded-xl border px-2 py-2 text-center text-sm ${isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`}
                  inputMode="numeric"
                  placeholder="9100"
                  title="Port"
                />
              </div>
              <p className="mt-2 text-xs opacity-70">
                {isDirectRj45
                  ? "Format TCP/IP direct (ex: 192.168.50.20:9100)."
                  : "Format TCP/IP (ex: 192.168.1.50:9100)."}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={onTest}
                  className="rounded-2xl border border-white/10 px-3 py-2 text-sm hover:border-amber-400/60"
                >
                  Tester
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-60"
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrinterModal = ({ isDarkMode, form, ipParts, setIpParts, onChange, onClose, onSave, onTest, saving, isEdit, targetOptions }) => {
  const updateIpPart = (index, value) => {
    const clean = value.replace(/[^0-9]/g, "").slice(0, 3);
    const next = [...ipParts];
    next[index] = clean;
    setIpParts(next);
    const joined = next.filter((p) => p !== "").join(".");
    onChange({ ...form, ip_address: joined });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${cardBg(isDarkMode)} max-w-2xl w-full rounded-3xl shadow-2xl p-6 space-y-5`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Imprimante IP</p>
            <h3 className="text-xl font-semibold">{isEdit ? "Modifier" : "Ajouter"} une imprimante</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LabeledInput
            label="Nom"
            isDarkMode={isDarkMode}
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            required
          />
          <LabeledSelect
            label="Cible"
            isDarkMode={isDarkMode}
            value={form.role}
            onChange={(e) => onChange({ ...form, role: e.target.value })}
            options={targetOptions}
          />

          <div className="md:col-span-2 space-y-2">
            <label className="text-sm opacity-70">Adresse IP</label>
            <div className="flex items-center gap-2">
              {ipParts.map((part, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  value={part}
                  onChange={(e) => updateIpPart(idx, e.target.value)}
                  className={`w-16 text-center rounded-2xl border px-3 py-2 text-sm ${
                    isDarkMode ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200 text-slate-900"
                  }`}
                  placeholder="0"
                />
              ))}
            </div>
            <p className="text-xs opacity-60">Exemple : 192 . 168 . 1 . 87</p>
          </div>

          <LabeledInput
            label="Port (optionnel)"
            type="number"
            isDarkMode={isDarkMode}
            value={form.port}
            onChange={(e) => onChange({ ...form, port: e.target.value })}
          />
          <LabeledInput
            label="Nombre de copies"
            type="number"
            min="1"
            step="1"
            isDarkMode={isDarkMode}
            value={form.copies || 1}
            onChange={(e) => onChange({ ...form, copies: normalizeCopies(e.target.value) })}
          />
          <label className="text-sm space-y-1">
            <span className="opacity-70">Activée</span>
            <div className="flex items-center h-full">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => onChange({ ...form, is_active: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs opacity-70">
            <AlertTriangle className="w-4 h-4" />
            Une zone (bar/cuisine) peut avoir 1 imprimante et/ou 1 KDS.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onTest}
              className="rounded-2xl border border-white/10 px-4 py-2 hover:border-amber-400/60"
            >
              Tester
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-white/10 px-4 py-2 hover:border-slate-400/40"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-white shadow-lg disabled:opacity-60"
            >
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LabeledInput = ({ label, ...props }) => (
  <label className="text-sm space-y-1">
    <span className="opacity-70">{label}</span>
    <input
      {...props}
      className={`w-full rounded-2xl border px-3 py-2 text-sm ${
        props.isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
      }`}
    />
  </label>
);

const LabeledSelect = ({ label, options, isDarkMode, ...props }) => (
  <label className="text-sm space-y-1">
    <span className="opacity-70">{label}</span>
    <select
      {...props}
      className={`w-full rounded-2xl border px-3 py-2 text-sm ${
        isDarkMode ? "bg-white/5 border-white/10" : "bg-white border-slate-200"
      }`}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </label>
);

export default PrinterManagement;
